const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

export function formatPrice(n: number | null | undefined): string {
  if (n == null) return '—'
  return currencyFmt.format(n)
}

// Two cached formatters so `formatCents` never builds an Intl instance per call.
// `usdWhole` is only ever reached when the amount is an exact number of dollars,
// so its maximumFractionDigits:0 can never round a real cent away.
const usdWhole = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})
const usdExact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export type FormatCentsOptions = {
  /**
   * Render the exact amount, always to the cent (`$50.00`). Use on portal
   * back-office surfaces, where a dealer reconciling numbers needs to see the
   * trailing zeros. Leave off for customer-facing surfaces, which show whole
   * dollars and only surface cents when they are genuinely non-zero.
   */
  exact?: boolean
  /** Rendered for null/undefined. Defaults to an em dash, like `formatPrice`. */
  nullish?: string
  /** Rendered for exactly zero (e.g. `'Free'`). Defaults to the formatted zero. */
  zero?: string
}

/**
 * Format an integer number of CENTS as a USD string.
 *
 * This is the one money formatter for cent-denominated amounts. It encodes the
 * product's pricing rule: customer-facing surfaces show whole dollars, and
 * render cents only when they are genuinely non-zero — so a $50 rental reads
 * `$50`, not `$50.00`, while a $49.50 rental reads `$49.50` rather than being
 * rounded to `$50`. Portal back-office surfaces pass `{ exact: true }`.
 *
 * Takes CENTS, not dollars. For an amount already in dollars use `formatPrice`.
 * Never route a Stripe charge amount through this — those stay integer cents.
 */
export function formatCents(
  cents: number | null | undefined,
  opts: FormatCentsOptions = {},
): string {
  if (cents == null) return opts.nullish ?? '—'
  if (cents === 0 && opts.zero !== undefined) return opts.zero
  const fmt = opts.exact || cents % 100 !== 0 ? usdExact : usdWhole
  return fmt.format(cents / 100)
}

/**
 * Compare two money amounts for equality to the cent.
 *
 * Use this instead of a bare `===`/`!==` when the two amounts may come from
 * different float computation paths (e.g. a Stripe charge persisted as
 * amount_total(cents)/100 vs a quote total summed from cent-clean line items).
 * Those paths can differ in the last float ULP for the same real value, and a
 * bare comparison would treat that sub-cent residue as a real difference —
 * e.g. showing an outstanding balance on a fully-paid order.
 */
/**
 * A timestamp shown as a calendar day, in an EXPLICIT zone. The zone is a
 * parameter on purpose: `toLocaleDateString()` with no arguments answers from
 * whatever machine is running it, and the storefront runs on two — Vercel (UTC)
 * builds the page, the visitor's browser (their zone, their locale) rebuilds
 * it, and React treats any difference as a corrupted page. Deterministic
 * formatting is what keeps the two builds identical. Pass the dealer's
 * `tenants.timezone` (or a location's) for customer-facing business dates;
 * pass 'UTC' for editorial dates that carry no clock.
 *
 *   numeric → "9/5/2026"      medium → "Sep 5, 2026"
 */
export type DateInZoneStyle = 'numeric' | 'medium'

const dateInZoneFmts = new Map<string, Intl.DateTimeFormat>()

export function formatDateInZone(
  when: string | Date | null | undefined,
  zone: string,
  style: DateInZoneStyle = 'numeric',
): string {
  if (when == null) return ''
  const d = when instanceof Date ? when : new Date(when)
  if (!Number.isFinite(d.getTime())) return ''
  const key = `${zone}|${style}`
  let fmt = dateInZoneFmts.get(key)
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      year: 'numeric',
      month: style === 'medium' ? 'short' : 'numeric',
      day: 'numeric',
    })
    dateInZoneFmts.set(key, fmt)
  }
  return fmt.format(d)
}

export function moneyEquals(a: number, b: number): boolean {
  return Math.round(a * 100) === Math.round(b * 100)
}

/** Format E.164 US numbers as (NXX) NXX-XXXX, pass others through */
export function formatPhone(phone: string): string {
  const match = phone.match(/^\+1(\d{3})(\d{3})(\d{4})$/)
  if (match) return `(${match[1]}) ${match[2]}-${match[3]}`
  return phone
}

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

/**
 * Escape the five HTML metacharacters (`& < > " '`) for safe interpolation of a
 * dealer/user-authored string into HTML text OR an attribute. The single canon
 * for HTML emails + rendered pages — a stored-injection / phishing seam otherwise.
 *
 * The Vercel serverless bundle carries an inlined copy at
 * `apps/portal/api/_lib/escapeHtml.ts` (guarded by
 * `apps/portal/tests/unit/escapeHtmlInline.test.ts`). Distinct from `escapeXml`,
 * which encodes `'` as `&apos;` (valid in XML, not in legacy HTML).
 */
export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => HTML_ESCAPES[c])
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const

/**
 * Human-readable byte size (base-1024): `0 B`, `512 B`, `1.5 KB`, `3.2 MB`, …
 * Bytes render as a whole number; KB and up carry one decimal. `null`/`undefined`
 * → the em-dash placeholder (`—`) — the calm "no value" convention. The single
 * canon for every attachment / media / storage size display.
 */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—'
  if (bytes === 0) return '0 B'
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    BYTE_UNITS.length - 1,
  )
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${BYTE_UNITS[i]}`
}

/**
 * Compact "time ago" for an ISO timestamp: `just now` / `Ns` / `Nm` / `Nh` /
 * `Nd` / `Nw`, falling back to a short calendar date (with year only when >1yr
 * old) past 30 days. The single canon for activity feeds / list "updated" cells.
 * Callers that can receive null guard it themselves (the empty-state label is a
 * per-surface choice — `—`, `never`, …).
 */
export function relativeTime(iso: string): string {
  const now = Date.now()
  const then = new Date(iso).getTime()
  const sec = Math.floor((now - then) / 1000)
  if (sec < 30) return 'just now'
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  if (day < 7) return `${day}d ago`
  if (day < 30) return `${Math.floor(day / 7)}w ago`
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: now - then > 365 * 86400000 ? 'numeric' : undefined,
  })
}
